import {find} from 'lodash';
import {connect} from 'react-redux';

import {
  createSelector,
  createTailStrictEqualSelector,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {
  DraftMessageModel,
  MessageVisualStatusesEnum,
  TimelineMessageModel,
} from '../../../../models/api/messageModel';
import {VisualRecipientModel} from '../../../../models/api/visualRecipientModel';
import {AppState} from '../../../../store/appState';
import {getShouldReplyWithOutlookStyle} from '../../../../store/data/company/companySelectors';
import {findMessageRecipients} from '../../../../store/data/contacts/recipientsSelectors';
import {getConversations, getMessages} from '../../../../store/data/dataSelectors';

/*
 * Props.
 */

interface ReplyToEnhancerProps {
  message: DraftMessageModel;
}

interface ReplyToEnhancerStateProps {
  replyToMessage: TimelineMessageModel | undefined;
  replyToRecipients: VisualRecipientModel[] | undefined;
  shouldReplyWithOutlookStyle: boolean;
}

export type ReplyToEnhancerInjectedProps = ReplyToEnhancerStateProps;

/*
 * Selectors.
 */

function getConversationId(_state: AppState, props: ReplyToEnhancerProps) {
  return props.message.conversationId;
}

function makeGetConversation() {
  return createSelector(
    [getConversations, getConversationId],
    (conversations, conversationId) => conversations[conversationId],
  );
}

function getInReplyToId(_state: AppState, props: ReplyToEnhancerProps) {
  return props.message.inReplyToId;
}

function makeGetReplyToMessage() {
  return createTailStrictEqualSelector([getMessages, getInReplyToId], (messages, inReplyToId) => {
    if (!inReplyToId) {
      return undefined;
    }

    const message = find(messages, (m) => Boolean(m && m.id === inReplyToId));
    if (!message || message.visualStatus !== MessageVisualStatusesEnum.TIMELINE) {
      return undefined;
    }

    return message;
  });
}

function makeMaybeGetVisualRecipients(
  getConversation: ReturnType<typeof makeGetConversation>,
  getMessage: ReturnType<typeof makeGetReplyToMessage>,
) {
  return createTailStrictEqualSelector(
    [(state) => state, getConversation, getMessage],
    (state, conversation, message) => {
      if (!conversation || !message) {
        return undefined;
      }

      return findMessageRecipients(state, conversation.namespace, message);
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ReplyToEnhancerProps>()(() => {
  const getConversation = makeGetConversation();
  const getReplyToMessage = makeGetReplyToMessage();

  return {
    replyToMessage: getReplyToMessage,
    replyToRecipients: makeMaybeGetVisualRecipients(getConversation, getReplyToMessage),
    shouldReplyWithOutlookStyle: getShouldReplyWithOutlookStyle,
  };
});

/** Provides props with information about the message being replied to or forwarded, if applicable. */
export const withReplyTo = connect<ReplyToEnhancerStateProps, {}, ReplyToEnhancerProps, AppState>(
  mapStateToProps,
);
